import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type { Location, UpdateLocationStatusInput, CreateLocationInput, LocationErrorCode, ApiEnvelope } from "@/domain/types";
import axios from "axios";

export function createLocationError(code: LocationErrorCode): Error {
  const err = new Error(code);
  err.name = "LocationError";
  return err;
}

export function isLocationError(err: unknown): err is Error & { message: LocationErrorCode } {
  return err instanceof Error && err.name === "LocationError";
}

function mapError(statusCode: number, data?: unknown): LocationErrorCode {
  if (statusCode === 409) {
    const msg = typeof data === "object" && data !== null ? (data as Record<string, unknown>).message : "";
    if (typeof msg === "string" && msg.includes("inventory")) return "HAS_INVENTORY";
    return "DUPLICATE_CODE";
  }
  if (statusCode === 400) {
    const msg = typeof data === "object" && data !== null ? (data as Record<string, unknown>).message : "";
    if (typeof msg === "string" && msg.includes("isle")) return "ISLE_NOT_FOUND";
    return "VALIDATION";
  }
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listLocationsByIsle(isleId: string): Promise<Location[]> {
  const response = await warehouseClient.get<ApiEnvelope<{ items: Location[]; meta: unknown }>>(`/location?isleId=${isleId}&limit=500`);
  const data = response.data.data;
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

export async function listLocationsByWarehouse(warehouseId: string): Promise<Location[]> {
  const response = await warehouseClient.get<ApiEnvelope<{ items: Location[]; meta: unknown }>>(`/location?warehouseId=${warehouseId}&limit=500`);
  const data = response.data.data;
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

export async function listLocationsPaginated(params: {
  page?: number;
  limit?: number;
  warehouseId?: string;
  isleId?: string;
  search?: string;
  status?: string;
}): Promise<{ items: Location[]; meta: { totalItems: number; itemsPerPage: number; totalPages: number; currentPage: number } }> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.warehouseId) query.set("warehouseId", params.warehouseId);
  if (params.isleId) query.set("isleId", params.isleId);
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);
  const response = await warehouseClient.get<ApiEnvelope<{ items: Location[]; meta: { totalItems: number; itemsPerPage: number; totalPages: number; currentPage: number } }>>(`/location?${query.toString()}`);
  const data = response.data.data;
  if (Array.isArray(data)) return { items: data, meta: { totalItems: data.length, itemsPerPage: data.length, totalPages: 1, currentPage: 1 } };
  return data;
}

export async function updateLocationStatus(input: UpdateLocationStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/location/status", input);
  } catch (err) {
    const code = extractStatusCode(err);
    const data = axios.isAxiosError(err) ? err.response?.data : undefined;
    throw createLocationError(mapError(code, data));
  }
}

export async function createLocation(input: CreateLocationInput): Promise<void> {
  try {
    await warehouseClient.post("/location", input);
  } catch (err) {
    const code = extractStatusCode(err);
    const data = axios.isAxiosError(err) ? err.response?.data : undefined;
    throw createLocationError(mapError(code, data));
  }
}
